import React, {useState} from 'react';
import {formatDateTime, relativeTime} from '../lib/time';
import type {FileVersion} from './VersionHistory';

/**
 * The dashboard's single right-side slide-over — consolidates version history (admin) and the Manage
 * link. The portfolio filter has moved to `DashboardHeader`'s pill switcher. Version history is a
 * collapsible section with its own scroll area so a long upload list never crowds out the actions
 * below it. Mirrors the VersionHistory / DevToolbar slide-over pattern.
 */
export function DashboardSidebar({
  open,
  onClose,
  isAdmin,
  versions,
  selectedVersion,
  onSelectVersion,
  onManage,
  onSignOut
}: {
  open: boolean;
  onClose: () => void;
  isAdmin: boolean;
  versions: FileVersion[];
  selectedVersion: string;
  onSelectVersion: (uploadedAt: string) => void;
  onManage: () => void;
  onSignOut: () => void;
}): React.ReactElement | null {
  const [historyExpanded, setHistoryExpanded] = useState(true);
  if (!open) return null;

  const latest = versions[0]?.uploadedAt;
  const showHistory = versions.length > 0;

  return (
    <div className="fixed inset-0 z-30">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <aside
        className="absolute right-0 top-0 h-full w-[380px] max-w-[90vw] bg-white shadow-xl flex flex-col"
        role="dialog"
        aria-label="Dashboard menu"
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-gray-200 shrink-0">
          <div className="text-sm font-bold text-gray-900">Menu</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2" aria-label="Close">
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {showHistory && (
            <section className="border-b border-gray-100 py-3">
              <button
                onClick={() => setHistoryExpanded(v => !v)}
                className="w-full flex items-center justify-between px-5 py-1 text-left"
                aria-expanded={historyExpanded}
              >
                <SectionLabel asChild>
                  Version history <span className="text-gray-400 font-normal normal-case">({versions.length})</span>
                </SectionLabel>
                <span className={`text-gray-400 transition-transform ${historyExpanded ? 'rotate-90' : ''}`}>›</span>
              </button>
              {historyExpanded && (
                <ul className="mt-1 max-h-72 overflow-y-auto">
                  {versions.map(v => {
                    const isCurrent = v.uploadedAt === latest;
                    const isSelected = v.uploadedAt === (selectedVersion || latest);
                    return (
                      <li key={v.uploadedAt}>
                        <button
                          onClick={() => onSelectVersion(v.uploadedAt)}
                          className={`w-full text-left px-5 py-2.5 border-l-2 transition-colors ${
                            isSelected ? 'border-blue-700 bg-blue-50' : 'border-transparent hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${isCurrent ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <span className="text-sm font-semibold text-gray-900">{relativeTime(v.uploadedAt)}</span>
                            {isCurrent && (
                              <span className="text-[10px] font-bold uppercase tracking-wide text-green-700 bg-green-100 rounded px-1.5 py-0.5">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 ml-4">
                            {formatDateTime(v.uploadedAt)} · {v.uploadedBy}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {isAdmin && (
            <section className="py-3">
              <SectionLabel>Admin</SectionLabel>
              <button
                onClick={onManage}
                className="w-full text-left px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
              >
                Manage console →
              </button>
            </section>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 shrink-0">
          <button
            onClick={onSignOut}
            className="w-full text-center text-sm font-semibold text-gray-700 border border-gray-300 rounded-md py-2 hover:bg-gray-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      </aside>
    </div>
  );
}

function SectionLabel({children, asChild = false}: {children: React.ReactNode; asChild?: boolean}): React.ReactElement {
  const cls = 'text-[11px] font-bold uppercase tracking-wide text-gray-400';
  return asChild ? <span className={cls}>{children}</span> : <div className={`${cls} px-5 mb-1`}>{children}</div>;
}
